import { FileSelectSmall } from '../FileViewer/FileSelectSmall';
import { FilterFeatures } from '../FilterFeatures';
import { GeoLevelPicker } from '../GeoLevelPicker';
import { BookIcon } from '../Icons/BookIcon';
import { DataPackageSelect } from './DataPackageSelect';
import { VarSelect } from '../VarSelect';
import { LanguageSwitch } from '../LanguageSwitch';
import { useFullScreen } from '../../hooks/use-fullscreen';
import { useState } from 'react';
import { ApiConfig, DataOptions, DataPackage, Indicator } from '../../types';
import { FEATURES } from '../../constants';
import { PackageIcon } from '../Icons/PackageIcon';
import { useTranslation } from 'react-i18next';
import { File } from '../FileViewer';
import { XAxisIcon, YAxisIcon, SizeIcon, ColorIcon } from '../Icons';
import type { Feature as GeoJSONFeature } from 'geojson';
import { Data } from '../../hooks/use-data2.hook';
import { Link } from '@tanstack/react-router';
import { useHandleResize } from '../../hooks/use-handle-resize';
import { ChrevronLeft } from '../Icons/ChevronLeft';
import { useShallow } from 'zustand/shallow';
import { useToolStore } from './tool.store';
import { FileSelect2 } from '../FileViewer/FileSelect2';

export function Menu(props: {
    timeRange: [number, number];
    config: ApiConfig;
    selectedDataPackage: DataPackage | null;
    dataPackages: DataPackage[];
    indicators: Indicator[];
    onSetSelectedDatapackage: (dataPackage: DataPackage) => void;
    dataOptions: DataOptions;
    points: GeoJSONFeature[];
    onClearSelectedFeatures: () => void;
    data: Data;
}) {
    const toolStore = useToolStore(
        useShallow((state) => ({
            timeRangeMap: state.timeRangeMap,
            indicatorsMap: state.indicatorsMap,
            setGeoLevel: state.setGeoLevel,
            setFeatureFilter: state.setFeatureFilter,
            featureFilter: state.featureFilter,
            lanName: state.lanName,
            shouldZoomToFiltered: state.shouldZoomToFiltered,
            geoLevel: state.geoLevel,
            mapGeoLevel: state.mapGeoLevel,
            setMapGeoLevel: state.setMapGeoLevel,
            toggleShouldZoomToFiltered: state.toggleShouldZoomToFiltered,
            setIndicatorById: state.setIndicatorById,
            getDataOptions: state.getDataOptions,
        }))
    );

    const fullscreenHandle = useFullScreen();
    const [showMenu, setShowMenu] = useState(false);
    const { t, i18n } = useTranslation();

    // With the län picker there is always more than one option to choose from.
    const shouldShowGeolevelPicker =
        props.config.geoLevels.length > (FEATURES.LAN_PICKER ? 0 : 1);

    const dataPackageMap = props.dataPackages.reduce((obj, curr) => {
        obj[curr.id] = curr;
        return obj;
    }, {} as Record<string, DataPackage>);

    useHandleResize(() => {
        setShowMenu(false);
    });

    function handleChangeFilter(newFilter: string[]) {
        toolStore.setFeatureFilter(newFilter);
    }

    function handleToggleZoomToFiltered() {
        toolStore.toggleShouldZoomToFiltered();
    }

    const dataOptions = toolStore.getDataOptions();

    const isJarfalla = window.location.host === 'xn--jrflla-buac.statsmap.org';

    return (
        <div
            className={`w-12 ${
                !fullscreenHandle.active && 'xl:w-auto'
            } relative`}
        >
            <div
                style={{
                    transform: showMenu ? 'translate(87%, 0)' : undefined,
                }}
                className={`
                absolute
                transition-transform
                right-full
                -mr-12
                isolate
                z-10
                bg-base-200
                p-2
                shadow-xl
                rounded-lg
                max-w-sm
                w-96
                h-full
                ${
                    !fullscreenHandle.active
                        ? `
                    xl:relative
                    xl:right-0
                    xl:mr-0
                    xl:bg-transparent
                    xl:p-0
                    xl:shadow-none
                    `
                        : undefined
                }
            `}
            >
                <MenuExpandButton
                    expanded={showMenu}
                    hidden={fullscreenHandle.active}
                    onClick={() => setShowMenu((value) => !value)}
                />

                {/* Left menu small */}
                <div
                    className={`
                    absolute
                    right-2
                    w-12
                    flex 
                    flex-col
                    h-full
                    gap-3
                    ${!showMenu ? '' : 'hidden'}
                    ${
                        !fullscreenHandle.active
                            ? `
                    xl:hidden
                    `
                            : 'justify-center xl:justify-start'
                    }
                `}
                >
                    <div className="gap-2 w-full rounded-lg bg-base-100 self-start flex flex-col items-center p-2">
                        {/* Data package select */}
                        <FileSelectSmall
                            className="btn-primary"
                            files={props.dataPackages.map((d): File => {
                                return {
                                    id: d.id,
                                    name: d.name[i18n.language] ?? d.name['sv'],
                                    type: 'file',
                                    meta:
                                        d.description?.[i18n.language] ??
                                        d.description?.['sv'],
                                };
                            })}
                            value={props.selectedDataPackage?.id ?? ''}
                            label={t('valj-datapaket')}
                            labelIcon={
                                <PackageIcon size={24} className="fill-white" />
                            }
                            onChange={(value) => {
                                const targetDataPackage = dataPackageMap[value];
                                props.onSetSelectedDatapackage(
                                    targetDataPackage
                                );
                            }}
                        />
                        {/* Y axis select */}
                        <FileSelect2
                            tiny
                            indicators={Object.values(
                                toolStore.indicatorsMap
                            )}
                            value={dataOptions.indicators.yVar.id}
                            label={t('valj-y')}
                            labelIcon={<YAxisIcon size={24} fill="white" />}
                            indicatorsMap={toolStore.indicatorsMap}
                            onChange={(value) => {
                                toolStore.setIndicatorById({
                                    key: 'y',
                                    indicatorId: value,
                                });
                            }}
                            tooltip={t('valj-y')}
                        />

                        {/* X axis select */}
                        <FileSelect2
                            tiny
                            indicators={Object.values(
                                toolStore.indicatorsMap
                            )}
                            value={props.dataOptions.indicators.xVar.id}
                            label={t('valj-x')}
                            labelIcon={<XAxisIcon size={24} fill="white" />}
                            indicatorsMap={toolStore.indicatorsMap}
                            onChange={(value) => {
                                toolStore.setIndicatorById({
                                    key: 'x',
                                    indicatorId: value,
                                });
                            }}
                            tooltip={t('valj-x')}
                        />

                        {/* R axis select */}
                        <FileSelect2
                            tiny
                            indicators={Object.values(
                                toolStore.indicatorsMap
                            )}
                            value={props.dataOptions.indicators.rVar.id}
                            label={t('valj-r')}
                            labelIcon={
                                <SizeIcon size={24} stroke="white" />
                            }
                            indicatorsMap={toolStore.indicatorsMap}
                            onChange={(value) => {
                                toolStore.setIndicatorById({
                                    key: 'r',
                                    indicatorId: value,
                                });
                            }}
                            tooltip={t('valj-r')}
                        />

                        {/* C axis select */}
                        <FileSelect2
                            tiny
                            indicators={Object.values(
                                toolStore.indicatorsMap
                            )}
                            value={
                                props.dataOptions.indicators.cVar?.id ??
                                'kommun'
                            }
                            label={t('valj-c')}
                            labelIcon={
                                <ColorIcon
                                    size={24}
                                    className="drop-shadow-lg"
                                />
                            }
                            indicatorsMap={toolStore.indicatorsMap}
                            onChange={(value) => {
                                toolStore.setIndicatorById({
                                    key: 'c',
                                    indicatorId: value,
                                });
                            }}
                            tooltip={t('valj-c')}
                            alternateActions={[
                                {
                                    label: 'Kommun',
                                    onClick: () => {
                                        toolStore.setIndicatorById({
                                            key: 'c',
                                            indicatorId: null,
                                        });
                                    },
                                },
                            ]}
                        />
                    </div>

                    {shouldShowGeolevelPicker && (
                        <div className="gap-2 w-full rounded-lg bg-base-100 self-start flex flex-col items-center p-2">
                            <GeoLevelPicker
                                vertical
                                dataOptions={props.dataOptions}
                                timeRange={toolStore.timeRangeMap}
                                geoLevels={props.config.geoLevels}
                                 geoLevel={toolStore.mapGeoLevel}
                                 onSetGeoLevel={toolStore.setMapGeoLevel}
                            />
                        </div>
                    )}

                    {!FEATURES.HIDE_FILTER_FEATURES_BOX && (
                        <div className="gap-2 w-full rounded-lg bg-base-100 self-start flex flex-col items-center p-2">
                            <FilterFeatures
                                tiny
                                points={props.points}
                                featureFilter={toolStore.featureFilter}
                                onToggleZoomToFiltered={
                                    handleToggleZoomToFiltered
                                }
                                onChangeFilter={handleChangeFilter}
                                zoomToFiltered={toolStore.shouldZoomToFiltered}
                                showZoomToggle
                            />
                        </div>
                    )}

                    <div className="gap-2 w-full rounded-lg bg-base-100 self-start flex flex-col items-center p-2">
                        <div
                            className="tooltip tooltip-right"
                            data-tip={t('om-verktyget')}
                        >
                            <Link
                                to="/"
                                aria-label={t('om-verktyget')}
                                className="btn btn-sm btn-secondary grid place-items-center w-full aspect-square"
                            >
                                <BookIcon
                                    size={24}
                                    className="fill-white"
                                />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Left menu full */}
                <div
                    className={`
                       flex
                       flex-col
                       gap-3
                        max-h-full
                        ${!showMenu && 'hidden'}
                        ${!fullscreenHandle.active && 'xl:flex'}
                   `}
                >
                    <div className="flex flex-row items-center gap-2 relative z-50">
                        <div className="flex-1">
                            <Link to="/" className="font-bold">{import.meta.env.VITE_SITE_NAME}</Link>
                        </div>
                        {FEATURES.LINK_TO_OM && (
                            <div className="tooltip tooltip-bottom" data-tip={t('om-verktyget')}>
                                <Link to="/" aria-label={t('om-verktyget')}>
                                    <button
                                        type="button"
                                        aria-hidden={true}
                                        tabIndex={-1}
                                        className="btn btn-sm btn-square btn-secondary"
                                    >
                                        <BookIcon size={20} className="fill-secondary-content" />
                                    </button>
                                </Link>
                            </div>
                        )}
                        {isJarfalla && (
                            <div className="tooltip tooltip-bottom" data-tip={t('om-verktyget')}>
                                <Link to="/jarfalla/guide" aria-label={t('om-verktyget')}>
                                    <button
                                        type="button"
                                        aria-hidden={true}
                                        tabIndex={-1}
                                        className="btn btn-sm btn-square btn-secondary"
                                    >
                                        <BookIcon size={20} className="fill-secondary-content" />
                                    </button>
                                </Link>
                            </div>
                        )}
                        <LanguageSwitch />
                    </div>
                    <div className="flex flex-col gap-3 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
                        <div className="bg-base-100 rounded-lg p-3 flex flex-col relative gap-3">
                            <DataPackageSelect
                                selectedDataPackage={props.selectedDataPackage}
                                setSelectedDatapackage={
                                    props.onSetSelectedDatapackage
                                }
                                dataPackageMap={dataPackageMap}
                                dataPackages={props.dataPackages}
                            />
                            <div className="z-50 pb-6">
                                <div className="divider my-0"></div>
                                <VarSelect
                                    indicatorsMap={toolStore.indicatorsMap}
                                    data={props.data}
                                    featureFilter={toolStore.featureFilter}
                                    geoLevel={toolStore.geoLevel}
                                />
                            </div>
                        </div>
                        {shouldShowGeolevelPicker && (
                            <div className="flex flex-row justify-center gap-2 items-center">
                                <GeoLevelPicker
                                    geoLevels={props.config.geoLevels}
                                    dataOptions={props.dataOptions}
                                    timeRange={toolStore.timeRangeMap}
                                     geoLevel={toolStore.mapGeoLevel}
                                     onSetGeoLevel={toolStore.setMapGeoLevel}
                                />
                            </div>
                        )}
                        {!FEATURES.HIDE_FILTER_FEATURES_BOX && (
                            <div className="bg-base-100 rounded-lg p-3">
                                <FilterFeatures
                                    points={props.points}
                                    featureFilter={toolStore.featureFilter}
                                    allAreasLabel={toolStore.lanName}
                                    zoomToFiltered={toolStore.shouldZoomToFiltered}
                                    onToggleZoomToFiltered={
                                        handleToggleZoomToFiltered
                                    }
                                    onChangeFilter={handleChangeFilter}
                                    showZoomToggle
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function MenuExpandButton(props: {
    expanded: boolean;
    hidden: boolean;
    onClick: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div
            className={`
                    absolute
                    left-full
                    top-16
                    -ml-2
                    mt-5
                    ${!props.expanded && 'rotate-180'}
                    ${!props.hidden && 'xl:hidden'}
                `}
        >
            <button
                onClick={props.onClick}
                type="button"
                aria-label={(props.expanded ? t('hide-menu') : t('show-menu')) as string}
                aria-expanded={props.expanded}
                className="btn btn-sm w-6 z-50 border-none bg-base-200 btn-secondary"
            >
                <div aria-hidden="true">
                    <ChrevronLeft fill="#e9498c" />
                </div>
            </button>
        </div>
    );
}
